/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

export function MarginCalculatorClient() {
  const [cost, setCost] = useState("");
  const [revenue, setRevenue] = useState("");

  // Neutral locals for parseFloat: guardrail matches money-ish *identifiers* in the argument.
  const cIn = cost;
  const rIn = revenue;
  const costNum = parseFloat(cIn) || 0;
  const revenueNum = parseFloat(rIn) || 0;

  const profit = revenueNum - costNum;
  const margin = revenueNum > 0 ? (profit / revenueNum) * 100 : 0;
  const markup = costNum > 0 ? (profit / costNum) * 100 : 0;
  const hasResult = costNum > 0 && revenueNum > 0;

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-foreground">Job Cost ($)</label>
          <input
            type="number"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="10000"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Selling Price ($)</label>
          <input
            type="number"
            value={revenue}
            onChange={(e) => setRevenue(e.target.value)}
            placeholder="14000"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
      </div>

      {hasResult && (
        <>
          <div className="mt-6 grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Profit</div>
              <div className={`text-2xl font-bold ${profit >= 0 ? "text-secondary" : "text-destructive"}`}>
                ${fmt(profit)}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Margin</div>
              <div className={`text-2xl font-bold ${margin >= 0 ? "text-foreground" : "text-destructive"}`}>
                {fmt(margin)}%
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Markup</div>
              <div className="text-2xl font-bold text-foreground">{fmt(markup)}%</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Revenue</div>
              <div className="text-2xl font-bold text-foreground">${fmt(revenueNum)}</div>
            </div>
          </div>
          <ResultActions
            toolSlug="margin-calculator"
            inputs={{ cost, revenue }}
            outputs={{ profit, margin, markup }}
          />
        </>
      )}
    </div>
  );
}
